type LegacySourceType = "GITHUB" | "CLOUD" | "GOOGLE_DRIVE";
type SourceProvider = "GITHUB" | "CLOUD" | "GOOGLE_DRIVE";
type SourceConnectionState = "connected" | "unavailable" | "not_applicable";

type ProjectSourceLike = {
  provider?: SourceProvider;
  label?: string;
  summary?: string | null;
  remoteLabel?: string | null;
  connectionState?: SourceConnectionState;
};

type ProjectCapabilitiesLike = {
  hasRemoteSource?: boolean;
  canSyncFromRemote?: boolean;
  canPublish?: boolean;
  canDiscardToRemote?: boolean;
  canConfigurePublishMode?: boolean;
};

type ProjectLike = {
  sourceType?: LegacySourceType;
  installationId?: number | null;
  repoOwner?: string | null;
  repoName?: string | null;
  source?: ProjectSourceLike | null;
  capabilities?: ProjectCapabilitiesLike | null;
};

export function getProjectSourceProvider(project: ProjectLike): SourceProvider {
  if (project.source?.provider) {
    return project.source.provider;
  }
  if (project.sourceType === "CLOUD") return "CLOUD";
  if (project.sourceType === "GOOGLE_DRIVE") return "GOOGLE_DRIVE";
  return "GITHUB";
}

export function isLocalProject(project: ProjectLike): boolean {
  return getProjectSourceProvider(project) === "CLOUD";
}

export function getProjectSourceLabel(project: ProjectLike): string {
  return (
    project.source?.label ??
    (isLocalProject(project)
      ? "Cloud project"
      : getProjectSourceProvider(project) === "GOOGLE_DRIVE"
        ? "Google Drive"
        : "GitHub")
  );
}

export function getProjectRemoteLabel(project: ProjectLike): string | null {
  if (project.source?.remoteLabel !== undefined) {
    return project.source.remoteLabel;
  }
  if (isLocalProject(project)) {
    return null;
  }
  if (getProjectSourceProvider(project) === "GOOGLE_DRIVE") {
    return project.source?.remoteLabel ?? null;
  }
  if (!project.repoOwner || !project.repoName) {
    return null;
  }
  return `${project.repoOwner}/${project.repoName}`;
}

export function getProjectGithubRepoUrl(project: ProjectLike): string | null {
  const remoteLabel = getProjectRemoteLabel(project);
  if (getProjectSourceProvider(project) !== "GITHUB" || !remoteLabel) {
    return null;
  }
  return `https://github.com/${remoteLabel}`;
}

export function getProjectSourceSummary(project: ProjectLike): string {
  if (project.source?.summary) {
    return project.source.summary;
  }
  const remoteLabel = getProjectRemoteLabel(project);
  if (!remoteLabel) {
    return "Cloud project stored in the app database.";
  }
  if (getProjectSourceProvider(project) === "GOOGLE_DRIVE") {
    return `Google Drive folder ${remoteLabel}`;
  }
  return `GitHub repository ${remoteLabel}`;
}

export function getProjectConnectionState(
  project: ProjectLike,
): SourceConnectionState {
  if (project.source?.connectionState) {
    return project.source.connectionState;
  }
  if (isLocalProject(project)) {
    return "not_applicable";
  }
  if (getProjectSourceProvider(project) === "GOOGLE_DRIVE") {
    return project.source?.connectionState ?? "unavailable";
  }
  return project.installationId ? "connected" : "unavailable";
}

export function canProjectSyncFromRemote(project: ProjectLike): boolean {
  return (
    project.capabilities?.canSyncFromRemote ??
    (getProjectSourceProvider(project) === "GOOGLE_DRIVE"
      ? project.source?.connectionState === "connected"
      : Boolean(project.installationId))
  );
}

export function canProjectPublish(project: ProjectLike): boolean {
  return (
    project.capabilities?.canPublish ??
    (getProjectSourceProvider(project) === "GOOGLE_DRIVE"
      ? project.source?.connectionState === "connected"
      : Boolean(project.installationId))
  );
}

export function canProjectDiscardToRemote(project: ProjectLike): boolean {
  return (
    project.capabilities?.canDiscardToRemote ??
    (getProjectSourceProvider(project) === "GOOGLE_DRIVE"
      ? project.source?.connectionState === "connected"
      : Boolean(project.installationId))
  );
}

export function canProjectConfigurePublishMode(project: ProjectLike): boolean {
  if (project.capabilities?.canConfigurePublishMode !== undefined) {
    return project.capabilities.canConfigurePublishMode;
  }
  return (
    !isLocalProject(project) &&
    getProjectSourceProvider(project) !== "GOOGLE_DRIVE"
  );
}
